import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAsync, useToast } from '../lib/store';
import { Button, EmptyState, ErrorState, Panel, Tabs } from '../components/ui';
import { timeAgo } from '../lib/format';

/**
 * The moderation dashboard.
 *
 * Deliberately not part of the Admin panel. Staff hold moderation permissions
 * and nothing else, and a shared page would mean every admin-only control on it
 * needed its own guard — the arrangement where one forgotten check hands a
 * moderator the economy tools. Separate page, separate router, separate
 * permission.
 *
 * The report queue is the point of the whole thing: `ChatReport` rows have been
 * accumulating since chat shipped with nothing able to read them.
 */

interface Overview {
  openReports: number;
  resolvedToday: number;
  activeMutes: number;
  actionsToday: number;
}

interface Report {
  id: string;
  reason: string;
  at: string;
  resolvedAt: string | null;
  reporter: string;
  message: {
    id: string;
    body: string;
    channel: string;
    at: string;
    deleted: boolean;
    author: string | null;
    authorId: string | null;
    authorPriorActions: number;
  };
}

type Tab = 'reports' | 'mutes' | 'history';

export function StaffPage() {
  const [tab, setTab] = useState<Tab>('reports');
  const overview = useAsync<Overview>(() => api.get('/staff/overview'));

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Moderation</h1>
          <div className="page-head__sub">
            {overview.data
              ? `${overview.data.openReports} open · ${overview.data.activeMutes} active mutes`
              : 'Reports, mutes and history.'}
          </div>
        </div>
      </div>

      {overview.data && (
        <Panel raised={false}>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
            <Stat label="Open reports" value={overview.data.openReports} loud={overview.data.openReports > 0} />
            <Stat label="Resolved today" value={overview.data.resolvedToday} />
            <Stat label="Active mutes" value={overview.data.activeMutes} />
            <Stat label="Actions today" value={overview.data.actionsToday} />
          </div>
        </Panel>
      )}

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'reports', label: 'Reports', badge: overview.data?.openReports || undefined },
          { value: 'mutes', label: 'Active mutes' },
          { value: 'history', label: 'History' },
        ]}
      />

      {tab === 'reports' && <ReportQueue onChanged={() => overview.reload()} />}
      {tab === 'mutes' && <MuteList />}
      {tab === 'history' && <HistoryList />}
    </div>
  );
}

function Stat({ label, value, loud }: { label: string; value: number; loud?: boolean }) {
  return (
    <div className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
      <div
        className="mono"
        style={{ fontSize: 'var(--text-lg)', color: loud ? 'var(--berry)' : undefined }}
      >
        {value}
      </div>
      <div className="tiny muted">{label}</div>
    </div>
  );
}

function ReportQueue({ onChanged }: { onChanged: () => void }) {
  const { push } = useToast();
  const [status, setStatus] = useState<'open' | 'resolved'>('open');
  const [busy, setBusy] = useState<string | null>(null);
  const state = useAsync<{ reports: Report[] }>(() => api.get('/staff/reports', { status }), [status]);

  async function resolve(report: Report, action: string, minutes?: number) {
    setBusy(report.id);
    try {
      await api.post(`/staff/reports/${report.id}/resolve`, { action, minutes });
      push('success', 'Report handled.');
      state.reload();
      onChanged();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not resolve that.');
    } finally {
      setBusy(null);
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  const reports = state.data?.reports ?? [];

  return (
    <Panel
      title="⚑ Reported messages"
      hint="Oldest first"
      action={
        <button
          type="button"
          className="linkbtn tiny"
          onClick={() => setStatus(status === 'open' ? 'resolved' : 'open')}
        >
          {status === 'open' ? 'Show resolved' : 'Show open'}
        </button>
      }
    >
      {state.loading ? (
        <div className="pxwell skeleton" style={{ height: 180 }} />
      ) : reports.length === 0 ? (
        <EmptyState art="✓" title={status === 'open' ? 'Queue is clear' : 'Nothing resolved yet'}>
          {status === 'open' ? 'No reports waiting. Nice.' : 'Resolved reports will appear here.'}
        </EmptyState>
      ) : (
        <div className="stack-sm">
          {reports.map((report) => (
            <div key={report.id} className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
              <div className="row row--between tiny muted">
                <span>
                  {report.message.channel} · reported by {report.reporter} {timeAgo(report.at)}
                </span>
                {report.message.authorPriorActions > 0 && (
                  <span className="chip chip--sm" title="Previous mutes and warnings">
                    {report.message.authorPriorActions} prior
                  </span>
                )}
              </div>

              <div className="pxwell" style={{ padding: 'var(--gap-sm)', margin: '6px 0' }}>
                <div className="tiny mono">
                  {report.message.author ?? 'system'}
                  {report.message.deleted && <span className="muted"> · already deleted</span>}
                </div>
                {/* The stored text, unmasked. A moderator judging a report needs
                    what was actually said, not what a reader was shown. */}
                <div className="tiny" style={{ marginTop: 2, wordBreak: 'break-word' }}>
                  {report.message.body}
                </div>
              </div>

              {report.reason && <div className="tiny muted">Reason given: “{report.reason}”</div>}

              {status === 'open' && (
                <div className="row row--wrap" style={{ gap: 6, marginTop: 8 }}>
                  <Button size="sm" onClick={() => void resolve(report, 'dismiss')} loading={busy === report.id}>
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    variant="info"
                    onClick={() => void resolve(report, 'delete')}
                    disabled={report.message.deleted}
                  >
                    Delete message
                  </Button>
                  {report.message.authorId && (
                    <>
                      <Button size="sm" variant="gold" onClick={() => void resolve(report, 'warn')}>
                        Warn
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => void resolve(report, 'delete-and-mute', 30)}
                      >
                        Delete + mute 30m
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function MuteList() {
  const state = useAsync<{
    mutes: Array<{
      id: string;
      username: string;
      scope: string;
      until: string;
      reason: string;
      moderator: string;
    }>;
  }>(() => api.get('/staff/mutes'));

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  const mutes = state.data?.mutes ?? [];

  return (
    <Panel title="🔇 Active mutes">
      {mutes.length === 0 ? (
        <EmptyState art="○" title="Nobody is muted">
          A quiet day.
        </EmptyState>
      ) : (
        <div className="stack-sm">
          {mutes.map((mute) => (
            <div key={mute.id} className="pxwell row row--between" style={{ padding: 'var(--gap-sm)' }}>
              <div>
                <div className="mono tiny">{mute.username}</div>
                <div className="tiny muted">
                  {mute.scope} · by {mute.moderator}
                  {mute.reason && ` · ${mute.reason}`}
                </div>
              </div>
              <span className="tiny mono">until {timeAgo(mute.until)}</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function HistoryList() {
  const [username, setUsername] = useState('');
  const [filter, setFilter] = useState('');
  const state = useAsync<{
    actions: Array<{
      id: string;
      kind: string;
      moderator: string;
      target: string;
      reason: string;
      at: string;
    }>;
  }>(() => api.get('/staff/history', { username: filter || undefined }), [filter]);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  const actions = state.data?.actions ?? [];

  return (
    <Panel title="◷ Moderation history">
      <form
        className="row row--wrap"
        style={{ marginBottom: 'var(--gap-sm)' }}
        onSubmit={(event) => {
          event.preventDefault();
          setFilter(username.trim());
        }}
      >
        <input
          className="input"
          style={{ flex: 1, minWidth: 180 }}
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          placeholder="Filter by username"
          aria-label="Filter by username"
        />
        <Button type="submit" size="sm">
          Filter
        </Button>
      </form>

      {actions.length === 0 ? (
        <EmptyState art="◌" title="Nothing recorded">
          Moderation actions appear here as they happen.
        </EmptyState>
      ) : (
        <div className="stack-sm">
          {actions.map((action) => (
            <div key={action.id} className="pxwell row row--between" style={{ padding: 'var(--gap-sm)' }}>
              <div>
                <div className="tiny">
                  <span className="chip chip--sm">{action.kind}</span> {action.target}
                </div>
                <div className="tiny muted">
                  by {action.moderator}
                  {action.reason && ` · ${action.reason}`}
                </div>
              </div>
              <span className="tiny muted">{timeAgo(action.at)}</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
